import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getPatientHistory } from "@/data/mockData";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";
import {
  FileText,
  Download,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  Minus,
  Calendar,
  Activity,
  Droplets,
  Heart
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const CURRENT_PATIENT_ID = "1";

export const PatientHistoryPage = () => {
  const history = getPatientHistory(CURRENT_PATIENT_ID);
  const [activeTab, setActiveTab] = useState("list");

  // Extract trends
  const glycemiaTrend = history
    .flatMap(h => h.results.filter(r => r.name === "Glicemia").map(r => ({
      date: format(new Date(h.date), "dd/MM/yy"),
      value: r.value
    })))
    .reverse();

  const bpTrend = history
    .flatMap(h => {
      const systolic = h.results.find(r => r.name === "Pressione Sistolica");
      const diastolic = h.results.find(r => r.name === "Pressione Diastolica");
      if (systolic && diastolic) {
        return [{
          date: format(new Date(h.date), "dd/MM/yy"),
          sistolica: systolic.value,
          diastolica: diastolic.value
        }];
      }
      return [];
    })
    .reverse();

  const getStatusIcon = (status) => {
    switch (status) {
      case "normal": return <CheckCircle2 className="h-4 w-4 text-success" />;
      case "high": return <TrendingUp className="h-4 w-4 text-warning" />;
      case "low": return <TrendingDown className="h-4 w-4 text-info" />;
      default: return <Minus className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "normal": 
        return <Badge className="bg-success/10 text-success border-success/20">Normale</Badge>;
      case "high": 
        return <Badge className="bg-warning/10 text-warning border-warning/20">Alto</Badge>;
      case "low": 
        return <Badge className="bg-info/10 text-info border-info/20">Basso</Badge>;
      default: 
        return <Badge variant="outline">N/A</Badge>;
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Storico Esami</h1>
        <p className="text-muted-foreground">Visualizza tutti i tuoi referti</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="list" className="gap-2">
            <FileText className="h-4 w-4" />
            Lista Esami
          </TabsTrigger>
          <TabsTrigger value="trends" className="gap-2">
            <Activity className="h-4 w-4" />
            Andamenti
          </TabsTrigger>
        </TabsList>

        {/* Exam List */}
        <TabsContent value="list" className="mt-6">
          {history.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">Nessun esame registrato</p>
              </CardContent>
            </Card>
          ) : (
            <Accordion type="single" collapsible className="space-y-4">
              {history.map((exam, index) => (
                <AccordionItem
                  key={exam.id}
                  value={exam.id}
                  className="border rounded-lg px-4 data-[state=open]:bg-muted/30"
                >
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-4 text-left">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">
                          {format(new Date(exam.date), "d MMMM yyyy", { locale: it })}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {exam.services.join(", ")}
                        </p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-4">
                    <div className="pt-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Parametro</TableHead>
                            <TableHead className="text-right">Valore</TableHead>
                            <TableHead className="text-right">Rif.</TableHead>
                            <TableHead className="text-right">Stato</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {exam.results.map((result, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-medium">{result.name}</TableCell>
                              <TableCell className="text-right">
                                <span className={cn(
                                  "font-semibold",
                                  result.status === "normal" && "text-success",
                                  result.status === "high" && "text-warning",
                                  result.status === "low" && "text-info"
                                )}>
                                  {result.value} {result.unit}
                                </span>
                              </TableCell>
                              <TableCell className="text-right text-muted-foreground text-sm">
                                {result.range}
                              </TableCell>
                              <TableCell className="text-right">
                                {getStatusBadge(result.status)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>

                      {exam.notes && (
                        <div className="mt-4 p-3 bg-muted rounded-lg">
                          <p className="text-sm">
                            <span className="font-medium">Note:</span> {exam.notes}
                          </p>
                        </div>
                      )}

                      <div className="mt-4 flex justify-end">
                        <Button variant="outline" size="sm" className="gap-2">
                          <Download className="h-4 w-4" />
                          Scarica PDF
                        </Button>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </TabsContent>

        {/* Trends */}
        <TabsContent value="trends" className="mt-6 space-y-6">
          {/* Glycemia Trend */}
          {glycemiaTrend.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Droplets className="h-5 w-5 text-info" />
                  Andamento Glicemia
                </CardTitle>
                <CardDescription>Storico dei valori glicemici</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={glycemiaTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[60, 150]} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }} 
                      />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="hsl(var(--info))"
                        strokeWidth={2}
                        dot={{ fill: 'hsl(var(--info))', strokeWidth: 2 }}
                        name="Glicemia (mg/dL)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Range normale:</span> 70-100 mg/dL a digiuno
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Blood Pressure Trend */}
          {bpTrend.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Heart className="h-5 w-5 text-destructive" />
                  Andamento Pressione
                </CardTitle>
                <CardDescription>Storico della pressione arteriosa</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={bpTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} domain={[50, 160]} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }} 
                      />
                      <Line
                        type="monotone"
                        dataKey="sistolica"
                        stroke="hsl(var(--destructive))"
                        strokeWidth={2}
                        dot={{ fill: 'hsl(var(--destructive))', strokeWidth: 2 }}
                        name="Sistolica (mmHg)"
                      />
                      <Line
                        type="monotone"
                        dataKey="diastolica"
                        stroke="hsl(var(--warning))"
                        strokeWidth={2}
                        dot={{ fill: 'hsl(var(--warning))', strokeWidth: 2 }}
                        name="Diastolica (mmHg)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 flex gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-destructive" />
                    <span className="text-sm">Sistolica</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-warning" />
                    <span className="text-sm">Diastolica</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {glycemiaTrend.length === 0 && bpTrend.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Activity className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">Dati insufficienti per visualizzare i trend</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PatientHistoryPage;
